import { useCallback, useEffect, useState } from 'react';
import { useAppState } from '../context/LoadTestContext';
import { fetchLoadTest, fetchLoadTestBoxes } from '../services/api';
import BoxPicker from '../load_test_components/v2/BoxPicker';
import DashboardBody from '../load_test_components/v2/DashboardBody';

// The graph-first load test dashboard for live tests: a box picker beside the
// header, then the graph, readings and load steps (DashboardBody, shared with
// past tests opened from Historical Data). It shows the picked box's newest
// test and refreshes every minute while the box is online. The classic
// LoadTestPage is switched off but kept in the code for now.

const ERROR_MESSAGES = {
  NO_DATALOGGER: 'There is no Data Logger assigned to this Load Test Box',
  NO_DATA: 'No data has been published for this Load Test Box yet. It will appear after the next refresh.',
  NO_TEST_DATA: 'This Load Test Box has no completed load test readings yet.',
  DATA_FORMAT: "This box's data file couldn't be read — its format isn't recognized. It may need attention on the logger side.",
  LOGGER_PROGRAM: "This box's data logger is running an older program whose readings the dashboard can't interpret reliably. The logger program needs to be updated before its data can be shown.",
};

function LoadTestPageV2() {
  const {
    loading, setLoading, applyPolledData, setSaveOptionsAnchor,
    setSelectedBox, stepSummaryData, dataWarnings, setDataWarnings,
    criticalDataWarnings, setCriticalDataWarnings,
  } = useAppState();

  const [testBoxes, setTestBoxes] = useState([]);
  const [selectedLoadTestBox, setSelectedLoadTestBox] = useState(null);
  const [error, setError] = useState(null);
  // null follows the newest step shown as readings arrive; a number is the
  // step (its number in step_summary) the user picked in the strip.
  const [pickedStep, setPickedStep] = useState(null);
  // Whether the Warnings tab has been opened for the box on screen; its count
  // pulses until then.
  const [warningsViewed, setWarningsViewed] = useState(false);
  const markWarningsRead = useCallback(() => setWarningsViewed(true), []);

  // Mirror the selected box into context so the HeaderBar (project dropdown) can
  // persist the chosen project against the right box.
  useEffect(() => { setSelectedBox(selectedLoadTestBox); }, [selectedLoadTestBox]);

  useEffect(() => {
    fetchLoadTestBoxes()
      .then((boxes) => {
        setTestBoxes(boxes);
        if (boxes.length > 0) setSelectedLoadTestBox(boxes[0]);
      })
      .catch((err) => { console.error(err); setError(err); });
  }, []);

  useEffect(() => {
    if (!selectedLoadTestBox) return undefined;
    // A response for a box that is no longer selected (picked another box
    // before it came back) is dropped, so it can't overwrite the new box's data
    // or show its error against it.
    let current = true;
    const loadData = async () => {
      try {
        // setLoading(true);
        const data = await fetchLoadTest(selectedLoadTestBox.id);
        if (!current) return;
        applyPolledData(data);
        setDataWarnings(data.warnings || []);
        setCriticalDataWarnings(data.critical_warnings || []);
      } catch (err) {
        if (!current) return;
        console.error(err);
        setError(err);

      } finally {
        if (current) setLoading(false);
      }
    };

    loadData();
    const intervalId = testBoxes.find((box) => box.id === selectedLoadTestBox.id)?.online
      ? setInterval(loadData, 60000)
      : null;
    return () => {
      current = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedLoadTestBox]);

  const selectBox = (box) => {
    setError(null);
    setSelectedLoadTestBox(box);
    setDataWarnings([]);
    setCriticalDataWarnings([]);
    setWarningsViewed(false);
  };

  return (
    <>
      <DashboardBody
        top={<BoxPicker boxes={testBoxes} selected={selectedLoadTestBox} onSelect={selectBox} />}
        box={selectedLoadTestBox}
        errorMessage={error ? (ERROR_MESSAGES[error.code] || 'An unexpected Error occurred.') : null}
        resetKey={selectedLoadTestBox?.id}
        warnings={{
          critical: criticalDataWarnings,
          warnings: dataWarnings,
          loading,
          unread: !warningsViewed,
          onOpen: markWarningsRead,
        }}
      />
    </>
  );
}

export default LoadTestPageV2;
